import { clog } from '@app/utils/clog';

const ALLOWED_PAYMENT_HOSTS = ['yoomoney.ru', 'yookassa.ru'];

export interface BillingPaymentNavigation {
  open: (url: string, target: string) => Window | null;
  assign: (url: string) => void;
}

export const getBillingPaymentUrl = (
  confirmationUrl: unknown
): string | null => {
  if (typeof confirmationUrl !== 'string') {
    clog('warn', '[billing-payment] confirmationUrl is not a string', {
      valueType: typeof confirmationUrl,
    });

    return null;
  }

  try {
    const url = new URL(confirmationUrl);
    const isAllowedHost = ALLOWED_PAYMENT_HOSTS.some(
      (host) => url.hostname === host || url.hostname.endsWith(`.${host}`)
    );

    if (url.protocol !== 'https:' || url.port || !isAllowedHost) {
      clog('warn', '[billing-payment] confirmationUrl rejected by validation', {
        hostname: url.hostname,
        protocol: url.protocol,
        port: url.port,
        isAllowedHost,
      });

      return null;
    }

    return url.href;
  } catch {
    clog('warn', '[billing-payment] confirmationUrl is not a valid URL', {
      confirmationUrl,
    });

    return null;
  }
};

export const openBillingPayment = (
  confirmationUrl: unknown,
  navigation?: BillingPaymentNavigation
): boolean => {
  const paymentUrl = getBillingPaymentUrl(confirmationUrl);
  if (!paymentUrl) return false;

  // Do not pass 'noopener'/'noreferrer' to window.open: with those features it
  // returns null even on a successful open, so the "popup blocked" fallback
  // below would fire every time and navigate the current tab too (double open).
  // We open without them and sever `opener` manually to keep the payment page
  // from reaching back into the CRM window.
  const paymentWindow = navigation
    ? navigation.open(paymentUrl, '_blank')
    : window.open(paymentUrl, '_blank');

  if (paymentWindow) {
    paymentWindow.opener = null;

    clog('info', '[billing-payment] opened payment in a new tab', {
      paymentUrl,
    });
  } else {
    clog(
      'warn',
      '[billing-payment] popup blocked, falling back to same-tab redirect',
      { paymentUrl }
    );

    if (navigation) {
      navigation.assign(paymentUrl);
    } else {
      window.location.assign(paymentUrl);
    }
  }

  return true;
};
